import { useState } from 'react';
import { Plus, Edit2, Trash2, Palette, Settings } from 'lucide-react';
import { MatterType, db } from '../../lib/db';
import { toast } from 'sonner';
import { MatterTypeFieldManager } from './MatterTypeFieldManager';

interface MatterTypeSettingsProps {
  userId: string;
  matterTypes: MatterType[];
  onUpdate: () => void;
}

export function MatterTypeSettings({ userId, matterTypes, onUpdate }: MatterTypeSettingsProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [editingType, setEditingType] = useState<MatterType | null>(null);
  const [managingFieldsFor, setManagingFieldsFor] = useState<MatterType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#6B7280',
  });

  const predefinedColors = [
    '#EF4444', // red
    '#F59E0B', // amber
    '#10B981', // emerald
    '#3B82F6', // blue
    '#8B5CF6', // violet
    '#EC4899', // pink
    '#6B7280', // gray
    '#059669', // green
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter a type name');
      return;
    }

    try {
      if (editingType) {
        await db.matterTypes.update(editingType.id, userId, {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          color: formData.color,
        });
        toast.success('Matter type updated successfully');
      } else {
        const maxPosition = Math.max(...matterTypes.map(t => t.position), -1);
        await db.matterTypes.create({
          user_id: userId,
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          color: formData.color,
          position: maxPosition + 1,
        });
        toast.success('Matter type created successfully');
      }
      
      resetForm();
      onUpdate();
    } catch (error) {
      console.error('Error saving matter type:', error);
      toast.error('Failed to save matter type');
    }
  };

  const handleDelete = async (typeId: string) => {
    if (!confirm('Are you sure you want to delete this matter type?')) {
      return;
    }

    try {
      await db.matterTypes.delete(typeId, userId);
      toast.success('Matter type deleted successfully');
      onUpdate();
    } catch (error) {
      console.error('Error deleting matter type:', error);
      toast.error('Failed to delete matter type');
    }
  };

  const startEdit = (type: MatterType) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      description: type.description || '',
      color: type.color || '#6B7280',
    });
    setIsCreating(true);
  };

  const resetForm = () => {
    setIsCreating(false);
    setEditingType(null);
    setFormData({
      name: '',
      description: '',
      color: '#6B7280',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Matter Types</h3>
          <p className="text-sm text-gray-600 mt-1">
            Create custom types to categorize your matters
          </p>
        </div>
        {!isCreating && (
          <button
            onClick={() => setIsCreating(true)}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Type
          </button>
        )}
      </div>

      {isCreating && (
        <form onSubmit={handleSubmit} className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Type Name
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                placeholder="e.g., Legal, Financial, Marketing"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description (Optional)
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                placeholder="Brief description of this type"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color
              </label>
              <div className="flex gap-2">
                {predefinedColors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({ ...formData, color })}
                    className={`w-8 h-8 rounded-full border-2 ${
                      formData.color === color ? 'border-gray-900' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700"
              >
                {editingType ? 'Update' : 'Create'} Type
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {matterTypes.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
            <p className="text-gray-500">No matter types yet. Create your first type to get started.</p>
          </div>
        ) : (
          matterTypes.map((type) => (
            <div
              key={type.id}
              className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-center space-x-3">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: type.color || '#6B7280' }}
                />
                <div>
                  <h4 className="font-medium text-gray-900">{type.name}</h4>
                  {type.description && (
                    <p className="text-sm text-gray-600">{type.description}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setManagingFieldsFor(type)}
                  className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  title="Manage custom fields"
                >
                  <Settings className="w-4 h-4" />
                </button>
                <button
                  onClick={() => startEdit(type)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Edit type"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(type.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete type"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {managingFieldsFor && (
        <MatterTypeFieldManager
          matterType={managingFieldsFor}
          onClose={() => setManagingFieldsFor(null)}
        />
      )}
    </div>
  );
}